'use client'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import clsx from 'clsx'
import { Button } from '@teqplay/ui'

import useDebounce from '@/app/hooks/useDebounce'
import { validateEmail, validatePhone } from '@/app/utils/validation'

import './InputTag.scss'

export type TPropsInputTag = {
  title: string
  subtitle?: string
  value: string[]
  onChange: (value: string[]) => void
  icon?: React.ReactNode
  validationType: 'email' | 'phone'
}

function InputTag({ title, subtitle, value, onChange, icon, validationType }: TPropsInputTag) {
  const inputTagRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState('')
  const debouncedQuery = useDebounce(inputValue, 500)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const t = useTranslations('ErrorMessage')

  // Helper to sanitize phone numbers by removing whitespace (matches validatePhone logic)
  const sanitizePhone = (phone: string): string => phone.replace(/\s+/g, '')

  const getValidationError = (input: string): string | null => {
    const trimmedInput = input.trim()
    if (!trimmedInput) return null // Don't show error for empty input

    // Check for duplicates first, sanitizing phone numbers before comparison
    if (validationType === 'phone') {
      const sanitizedInput = sanitizePhone(trimmedInput)
      const isDuplicate = value.some((v) => sanitizePhone(v) === sanitizedInput)
      if (isDuplicate) return t('thisAlreadyExists')
    } else {
      if (value.includes(trimmedInput)) return t('thisAlreadyExists')
    }

    switch (validationType) {
      case 'email':
        return validateEmail(trimmedInput) ? null : t('pleaseEnterValidEmailAddress')
      case 'phone':
        return validatePhone(trimmedInput) ? null : t('pleaseEnterValidPhoneNumber')
      default:
        return null
    }
  }

  const isValidInput = (input: string): boolean => {
    const trimmedInput = input.trim()
    if (!trimmedInput) return false

    // Check for duplicates first, sanitizing phone numbers before comparison
    if (validationType === 'phone') {
      const sanitizedInput = sanitizePhone(trimmedInput)
      const isDuplicate = value.some((v) => sanitizePhone(v) === sanitizedInput)
      if (isDuplicate) return false
    } else {
      if (value.includes(trimmedInput)) return false
    }

    switch (validationType) {
      case 'email':
        return validateEmail(trimmedInput)
      case 'phone':
        return validatePhone(trimmedInput)
      default:
        return true
    }
  }

  const handleAddTag = () => {
    if (inputValue.trim() && isValidInput(inputValue)) {
      const trimmedVal = inputValue.trim().replace(/,/g, '')
      onChange([...value, trimmedVal])
      setInputValue('')
      // Keep focus on input after adding tag
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }

  const onKeyInputPressed = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') &&
      inputValue.trim() &&
      isValidInput(inputValue)
    ) {
      e.preventDefault()
      handleAddTag()
    }
    // if key is backspace and inputValue is empty, remove last tag
    if (e.key === 'Backspace' && inputValue.trim() === '' && value.length > 0) {
      e.preventDefault()
      onChange(value.slice(0, -1))
    }
  }

  const handleRemoveTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag))
  }

  const onBlur = () => {
    setIsFocused(false)

    // Check for validation errors when input loses focus
    const validationError = getValidationError(inputValue)
    if (validationError) {
      setErrorMessage(validationError)
    } else {
      setErrorMessage(null)
      // Only auto-add if valid
      if (inputValue.trim() && isValidInput(inputValue)) {
        onChange([...value, inputValue.trim()])
        setInputValue('')
      }
    }
  }

  const isInputActive = useMemo(() => {
    return inputValue.trim() !== '' || value.length > 0 || isFocused
  }, [inputValue, value, isFocused])

  useEffect(() => {
    if (isInputActive) {
      // Scroll first to bring the header into a good position
      inputTagRef.current?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth'
      })
    }
  }, [isInputActive, debouncedQuery])

  useLayoutEffect(() => {
    if (isInputActive) {
      // Wait for layout to update before scrolling
      requestAnimationFrame(() => {
        inputTagRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
      })
    }
  }, [isInputActive])

  return (
    <div ref={inputTagRef} className="input-tag">
      <div
        className={clsx('input-tag__field', { active: isInputActive })}
        onClick={() => inputRef.current?.focus()}
      >
        <label className={clsx('input-tag__label text-body', { active: isInputActive })}>
          {icon && <div className="input-tag__icon">{icon}</div>}
          <span>{title}</span>
        </label>
        <div className={clsx('input-tag__field-input', { active: isInputActive })}>
          <span className="input-tag__tags">
            <span>{`${subtitle}:`}</span>
            {value.map((tag) => (
              <span className="input-tag__tag" key={tag}>
                <span>{tag}</span>
                <button className="input-tag__remove" onClick={() => handleRemoveTag(tag)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            ))}
            <input
              ref={inputRef}
              type="text"
              className={clsx('input-tag__input', { focus: isFocused })}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.currentTarget.value)
                // Clear error message when user starts typing
                if (errorMessage) {
                  setErrorMessage(null)
                }
              }}
              onKeyDown={onKeyInputPressed}
              onFocus={() => {
                setIsFocused(true)
                setErrorMessage(null) // Clear error when input gets focus
              }}
              onBlur={onBlur}
            />
          </span>
          {inputValue.trim().length > 0 && (
            <Button
              variant="brand-secondary"
              size="s"
              disabled={!isValidInput(inputValue)}
              onClick={handleAddTag}
            >
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          )}
        </div>
        {errorMessage && <div className="input-tag__error text-body-small">{errorMessage}</div>}
      </div>
    </div>
  )
}

export default InputTag
